import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate, useParams } from "react-router-dom";
import { settingsApi } from "../../api/settings";
import { formatApiError, getApiBaseUrl } from "../../api/client";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import {
  buildProjectSecurePreviewUrl,
  getProjectPublicUrl,
} from "../../utils/projectDeployment";
import { assetValueFromResponse, isManagedLandingAsset } from "../../utils/landingAssets";
import { TRIAL_LANDING_EVENT } from "../../utils/trialOnboarding";
import LandingSettingsDialog from "../../components/dashboard/landingEditor/LandingSettingsDialog";
import {
  emptySettings,
  mergeSettings,
} from "../../components/dashboard/landingEditor/config";
import { buildGoogleFontsUrl } from "../../components/dashboard/landingEditor/googleFonts";
import {
  findStepForSection,
  getSiteBuilderProgress,
} from "../../components/dashboard/landingEditor/siteBuilderModel";
import LandingStudioToolbar from "../../components/dashboard/landingStudio/LandingStudioToolbar";
import LandingStudioQuestionDock from "../../components/dashboard/landingStudio/LandingStudioQuestionDock";
import LandingStudioCanvasStage from "../../components/dashboard/landingStudio/LandingStudioCanvasStage";
import LandingCanvas from "../../components/dashboard/landingEditor/LandingCanvas";
import { getLandingSegments } from "../../components/dashboard/landingEditor/landingLayout";
import LandingSegmentInspector from "../../components/dashboard/landingStudio/LandingSegmentInspector";
import LandingElementInspector from "../../components/dashboard/landingStudio/LandingElementInspector";
import LandingRevisionsDrawer from "../../components/dashboard/landingStudio/LandingRevisionsDrawer";
import LandingAccessibilityDrawer from "../../components/dashboard/landingStudio/LandingAccessibilityDrawer";
import ProductsPageEditor from "../../components/dashboard/landingStudio/pageEditors/ProductsPageEditor";
import ProductPageEditor from "../../components/dashboard/landingStudio/pageEditors/ProductPageEditor";
import WebPushPromptEditor from "../../components/dashboard/landingStudio/pageEditors/WebPushPromptEditor";
import { buildLandingAccessibilityAudit } from "../../components/dashboard/landingStudio/landingAccessibilityAudit";
import { getLandingPreviewDevice } from "../../components/dashboard/landingStudio/previewDevices";
import { resolveNextStudioQuestion } from "../../components/dashboard/landingStudio/landingStudioQuestions";
import {
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
} from "../../components/dashboard/landingStudio/segmentRegistry";
import { getElementSectionContext } from "../../components/dashboard/landingStudio/elementEditing";
import {
  setManyValuesAtPaths,
  setValueAtPath,
} from "../../components/dashboard/landingStudio/landingStudioUtils";

import {
  draftSettingsPayload,
  deploymentFromPublishResponse,
  deploymentJobFromPublishResponse,
  deploymentJobId,
  isPendingDeploymentJob,
  deploymentJobFailure,
  openPreviewPlaceholder,
  collectManagedAssets,
  sameEditorValue,
  preserveVersionMetadata
} from "../../components/dashboard/landingStudio/editor/landingEditorPageUtils";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function ProjectLandingVisualEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const mobileViewport = useMediaQuery("(max-width:700px)");
  const tabletViewport = useMediaQuery("(max-width:1100px)");
  const {
    projects,
    activeProject,
    setActiveProject,
    loadProjects,
    refreshProjectDeployment,
    retryProjectDeployment,
  } = useProjects();

  const [project, setProject] = React.useState(null);
  const [form, setForm] = React.useState(emptySettings);
  const [device, setDevice] = React.useState(() => (
    mobileViewport ? "mobile" : tabletViewport ? "tablet" : "desktop"
  ));
  const [editorSurface, setEditorSurface] = React.useState("landing");
  const previewDevice = getLandingPreviewDevice(device);
  const previewHorizontalGutter = previewDevice.id === "mobile" ? 0 : 32;
  const [activeSection, setActiveSection] = React.useState("");
  const [selectedElement, setSelectedElement] = React.useState(null);
  const [elementShellSectionId, setElementShellSectionId] = React.useState("");
  const [elementInspectorOpen, setElementInspectorOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [savingPath, setSavingPath] = React.useState("");
  const [statusTransition, setStatusTransition] = React.useState("");
  const [previewing, setPreviewing] = React.useState(false);
  const [openingPreview, setOpeningPreview] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const formRef = React.useRef(emptySettings);
  const savedDraftRef = React.useRef(emptySettings);
  const dirtyRef = React.useRef(false);
  const pendingAssetDeletesRef = React.useRef(new Set());
  const editHistoryRef = React.useRef({
    undo: [],
    redo: [],
    lastGroupKey: "",
    lastRecordedAt: 0,
  });
  const [, setHistoryVersion] = React.useState(0);
  const [revisionsOpen, setRevisionsOpen] = React.useState(false);
  const [revisions, setRevisions] = React.useState([]);
  const [revisionsLoading, setRevisionsLoading] = React.useState(false);
  const [revisionsError, setRevisionsError] = React.useState("");
  const [restoringSiteRevision, setRestoringSiteRevision] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState(0);
  const [settingsMode, setSettingsMode] = React.useState("global");
  const [sectionId, setSectionId] = React.useState("hero");
  const [segmentInspectorOpen, setSegmentInspectorOpen] = React.useState(false);
  const [segmentInspectorTool, setSegmentInspectorTool] = React.useState("content");
  const [segmentInspectorImagePath, setSegmentInspectorImagePath] = React.useState("");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [publicationValidation, setPublicationValidation] = React.useState(null);
  const [publicationValidationLoading, setPublicationValidationLoading] = React.useState(false);
  const [publicationValidationError, setPublicationValidationError] = React.useState("");
  const [accessibilityOpen, setAccessibilityOpen] = React.useState(false);
  const [deploymentJob, setDeploymentJob] = React.useState(null);
  const [deploymentChecking, setDeploymentChecking] = React.useState(false);
  const [deploymentAction, setDeploymentAction] = React.useState("");
  const [deploymentMonitorError, setDeploymentMonitorError] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [activeProject, projectId, projects]
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const editorSurfaceOptions = React.useMemo(() => ([
    { id: "landing", label: "Landing" },
    ...(productsEnabled
      ? [
          { id: "products", label: "Productos" },
          { id: "product", label: "Detalle producto" },
        ]
      : []),
    { id: "notifications", label: "Notificaciones" },
  ]), [productsEnabled]);
  const publicUrl = getProjectPublicUrl(effectiveProject);
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const accessibilityAudit = React.useMemo(
    () => buildLandingAccessibilityAudit(form, productsEnabled),
    [form, productsEnabled]
  );
  const question = React.useMemo(
    () => resolveNextStudioQuestion(progress),
    [progress]
  );
  const saving = Boolean(savingPath);
  const publishing = statusTransition === "published";
  const changingPublicStatus = Boolean(statusTransition);
  const canUndo = editHistoryRef.current.undo.length > 0;
  const canRedo = editHistoryRef.current.redo.length > 0;

  React.useEffect(() => {
    formRef.current = form;
  }, [form]);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const selector = 'link[data-webprofit-landing-fonts="true"]';
    let link = document.head.querySelector(selector);
    const href = String(form?.theme?.googleFontsUrl || "").trim();
    if (!href) {
      link?.remove();
      return undefined;
    }
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.dataset.webprofitLandingFonts = "true";
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
    return undefined;
  }, [form?.theme?.googleFontsUrl]);

  React.useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  React.useEffect(() => {
    if (productsEnabled || editorSurface === "landing") return;
    setEditorSurface("landing");
  }, [editorSurface, productsEnabled]);

  React.useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  const applyDeploymentProject = React.useCallback(
    (nextProject) => {
      if (!nextProject) return null;
      setProject(nextProject);
      setActiveProject(nextProject);
      return nextProject;
    },
    [setActiveProject]
  );

  const refreshDeploymentState = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!projectId || deploymentChecking) return null;
      try {
        if (!silent) setDeploymentChecking(true);
        setDeploymentMonitorError("");
        const nextProject = await refreshProjectDeployment(projectId);
        return applyDeploymentProject(nextProject);
      } catch (err) {
        const nextError = formatApiError(
          err,
          "No se pudo consultar el estado del despliegue"
        );
        setDeploymentMonitorError(nextError);
        if (!silent) setError(nextError);
        return null;
      } finally {
        if (!silent) setDeploymentChecking(false);
      }
    },
    [applyDeploymentProject, deploymentChecking, projectId, refreshProjectDeployment]
  );

  const retryDeployment = React.useCallback(async () => {
    if (!projectId || deploymentAction) return null;
    try {
      setDeploymentAction("retry");
      setDeploymentMonitorError("");
      setError("");

      const failedJobId =
        deploymentJobFailure(deploymentJob) && deploymentJobId(deploymentJob);
      if (failedJobId) {
        const data = await projectsApi.retryDeploymentJob(projectId, failedJobId);
        const retriedJob = data?.job || null;
        if (retriedJob) setDeploymentJob(retriedJob);
        const nextProject = await refreshProjectDeployment(projectId).catch(() => null);
        if (nextProject) applyDeploymentProject(nextProject);
        setMessage(
          "El trabajo de publicación se volvió a poner en cola. Podés seguir su estado desde el editor."
        );
        return retriedJob || data;
      }

      const nextProject = await retryProjectDeployment(projectId);
      setDeploymentJob(null);
      applyDeploymentProject(nextProject);
      setMessage("El despliegue se volvió a iniciar. Podés seguir su estado desde el editor.");
      return nextProject;
    } catch (err) {
      const nextError = formatApiError(err, "No se pudo reintentar el despliegue");
      setDeploymentMonitorError(nextError);
      setError(nextError);
      return null;
    } finally {
      setDeploymentAction("");
    }
  }, [
    applyDeploymentProject,
    deploymentAction,
    deploymentJob,
    projectId,
    refreshProjectDeployment,
    retryProjectDeployment,
  ]);

  const trackedDeploymentJobId = deploymentJobId(deploymentJob);
  const trackedDeploymentJobPending = isPendingDeploymentJob(deploymentJob);

  React.useEffect(() => {
    if (!trackedDeploymentJobId || !trackedDeploymentJobPending) return undefined;

    let active = true;
    const pollJob = async () => {
      try {
        setDeploymentChecking(true);
        const data = await projectsApi.listDeploymentJobs(projectId, {
          type: deploymentJob?.type || "social_preview_refresh",
          limit: 20,
        });
        if (!active) return;
        const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
        const currentJob = jobs.find(
          (job) => deploymentJobId(job) === trackedDeploymentJobId
        );
        if (!currentJob) return;

        setDeploymentJob(currentJob);
        const failure = deploymentJobFailure(currentJob);
        if (failure) {
          setDeploymentMonitorError(failure);
          setError(`La versión quedó publicada, pero el despliegue falló: ${failure}`);
        } else {
          setDeploymentMonitorError("");
        }

        if (["completed", "failed"].includes(String(currentJob.status || "").toLowerCase())) {
          const nextProject = await refreshProjectDeployment(projectId).catch(() => null);
          if (active && nextProject) applyDeploymentProject(nextProject);
        }
      } catch (err) {
        if (active) {
          setDeploymentMonitorError(
            formatApiError(err, "No se pudo seguir el estado del despliegue")
          );
        }
      } finally {
        if (active) setDeploymentChecking(false);
      }
    };

    const initial = window.setTimeout(pollJob, 1800);
    const timer = window.setInterval(pollJob, 4500);
    return () => {
      active = false;
      window.clearTimeout(initial);
      window.clearInterval(timer);
    };
  }, [
    applyDeploymentProject,
    deploymentJob?.type,
    projectId,
    refreshProjectDeployment,
    trackedDeploymentJobId,
    trackedDeploymentJobPending,
  ]);

  React.useEffect(() => {
    const deploymentStatus = String(effectiveProject?.deployment?.status || "").toLowerCase();
    if (deploymentStatus !== "provisioning" || trackedDeploymentJobPending) return undefined;

    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const nextProject = await refreshProjectDeployment(projectId);
        if (active && nextProject) {
          applyDeploymentProject(nextProject);
          setDeploymentMonitorError("");
        }
      } catch {
        // El botón manual de actualizar conserva el error visible si hace falta.
      }
    }, 7000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [
    applyDeploymentProject,
    effectiveProject?.deployment?.status,
    projectId,
    refreshProjectDeployment,
    trackedDeploymentJobPending,
  ]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [loadProjects, projects.length]);

  React.useEffect(() => {
    let active = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!active) return;
        const nextProject = data.project || null;
        setProject(nextProject);
        if (nextProject) setActiveProject(nextProject);
      })
      .catch(() => null);

    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await settingsApi.get(projectId);
        if (!active) return;
        const merged = mergeSettings(data.settings);
        formRef.current = merged;
        savedDraftRef.current = merged;
        setForm(merged);
        dirtyRef.current = false;
        setDirty(false);
        editHistoryRef.current = {
          undo: [],
          redo: [],
          lastGroupKey: "",
          lastRecordedAt: 0,
        };
        setHistoryVersion((value) => value + 1);

        const initialProgress = getSiteBuilderProgress(merged, productsEnabled);
        const firstIncomplete = initialProgress.steps.find((step) => !step.done);
        const firstSection = firstIncomplete?.actions?.find((action) => action.type === "section")?.section;
        setActiveSection(firstSection || "hero");

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );
      } catch (err) {
        if (active) setError(err.message || "No se pudo cargar la configuración de la página");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [productsEnabled, projectId]);

  const applyDraftState = React.useCallback(
    (nextForm) => {
      const previousAssets = collectManagedAssets(formRef.current);
      const nextAssets = collectManagedAssets(nextForm);
      previousAssets.forEach((path) => {
        if (!nextAssets.has(path)) pendingAssetDeletesRef.current.add(path);
      });
      nextAssets.forEach((path) => pendingAssetDeletesRef.current.delete(path));

      formRef.current = nextForm;
      setForm(nextForm);
      const hasPendingChanges = !sameEditorValue(
        draftSettingsPayload(nextForm, productsEnabled),
        draftSettingsPayload(savedDraftRef.current, productsEnabled)
      );
      dirtyRef.current = hasPendingChanges;
      setDirty(hasPendingChanges);
      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: nextForm },
        })
      );
      return nextForm;
    },
    [productsEnabled, projectId]
  );

  const recordUndoCheckpoint = React.useCallback(
    (snapshot, groupKey = "edit", force = false) => {
      const history = editHistoryRef.current;
      const now = Date.now();
      const coalesced =
        !force &&
        groupKey &&
        history.lastGroupKey === groupKey &&
        now - history.lastRecordedAt < 900;

      if (!coalesced) {
        history.undo.push(snapshot);
        if (history.undo.length > 40) {
          history.undo.splice(0, history.undo.length - 40);
        }
      }
      history.redo = [];
      history.lastGroupKey = groupKey;
      history.lastRecordedAt = now;
      setHistoryVersion((value) => value + 1);
    },
    []
  );

  const stageDraft = React.useCallback(
    (nextForm, options = {}) => {
      const previous = formRef.current;
      if (
        sameEditorValue(
          draftSettingsPayload(previous, productsEnabled),
          draftSettingsPayload(nextForm, productsEnabled)
        )
      ) {
        return Promise.resolve(previous);
      }

      if (options.recordHistory !== false) {
        recordUndoCheckpoint(
          previous,
          options.historyKey || "edit",
          options.forceHistory === true
        );
      }
      return Promise.resolve(applyDraftState(nextForm));
    },
    [applyDraftState, productsEnabled, recordUndoCheckpoint]
  );

  const undoLastEdit = React.useCallback(() => {
    const history = editHistoryRef.current;
    if (!history.undo.length) return;

    const current = formRef.current;
    const previous = history.undo.pop();
    history.redo.push(current);
    if (history.redo.length > 40) history.redo.shift();
    history.lastGroupKey = "";
    history.lastRecordedAt = 0;

    applyDraftState(preserveVersionMetadata(previous, current));
    setHistoryVersion((value) => value + 1);
    setMessage("Se deshizo el último cambio. Guardá cuando quieras conservar esta recuperación.");
  }, [applyDraftState]);

  const redoLastEdit = React.useCallback(() => {
    const history = editHistoryRef.current;
    if (!history.redo.length) return;

    const current = formRef.current;
    const next = history.redo.pop();
    history.undo.push(current);
    if (history.undo.length > 40) history.undo.shift();
    history.lastGroupKey = "";
    history.lastRecordedAt = 0;

    applyDraftState(preserveVersionMetadata(next, current));
    setHistoryVersion((value) => value + 1);
    setMessage("Se rehizo el cambio.");
  }, [applyDraftState]);

  const saveDraft = React.useCallback(async () => {
    const snapshot = formRef.current;
    const pendingDeletesAtStart = new Set(pendingAssetDeletesRef.current);
    try {
      setSavingPath("draft");
      setError("");
      const data = await settingsApi.update(
        projectId,
        draftSettingsPayload(snapshot, productsEnabled)
      );
      const merged = mergeSettings(data.settings);
      const changedDuringSave = formRef.current !== snapshot;
      const nextForm = changedDuringSave
        ? preserveVersionMetadata(formRef.current, merged)
        : merged;
      savedDraftRef.current = merged;
      formRef.current = nextForm;
      setForm(nextForm);
      const hasPendingChanges = !sameEditorValue(
        draftSettingsPayload(nextForm, productsEnabled),
        draftSettingsPayload(merged, productsEnabled)
      );
      dirtyRef.current = hasPendingChanges;
      setDirty(hasPendingChanges);

      const currentAssets = collectManagedAssets(formRef.current);
      const pendingDeletes = Array.from(pendingDeletesAtStart).filter(
        (path) => isManagedLandingAsset(path) && !currentAssets.has(path)
      );
      pendingDeletesAtStart.forEach((path) => {
        if (!currentAssets.has(path)) pendingAssetDeletesRef.current.delete(path);
      });
      await Promise.allSettled(
        pendingDeletes.map((path) => landingAssetsApi.remove(projectId, path))
      );

      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: nextForm },
        })
      );
      setMessage(
        hasPendingChanges
          ? "Borrador guardado. Hay cambios nuevos todavía pendientes."
          : "Borrador guardado. La versión publicada no cambió."
      );
      return nextForm;
    } catch (err) {
      dirtyRef.current = true;
      setDirty(true);
      setError(err.message || "No se pudo guardar el borrador");
      return null;
    } finally {
      setSavingPath("");
    }
  }, [productsEnabled, projectId]);

  const saveSettingsDialog = React.useCallback(
    async (nextForm) => {
      await stageDraft(nextForm, {
        historyKey: "settings-dialog",
        forceHistory: true,
      });
      const saved = await saveDraft();
      if (!saved) {
        throw new Error("No se pudieron guardar los ajustes del borrador.");
      }
      return saved;
    },
    [saveDraft, stageDraft]
  );

  const commitPath = React.useCallback(
    (path, value) => {
      const next = setValueAtPath(formRef.current, path, value);
      return stageDraft(next, { historyKey: `path:${path}` });
    },
    [stageDraft]
  );

  const commitMany = React.useCallback(
    (changes) => {
      const next = setManyValuesAtPaths(formRef.current, changes);
      const keys = Object.keys(changes || {}).sort().join("|");
      return stageDraft(next, { historyKey: `group:${keys || "changes"}` });
    },
    [stageDraft]
  );

  const uploadAssetFile = React.useCallback(
    async (file, metadata = {}) => {
      const response = await landingAssetsApi.upload(projectId, file, {
        pageKey: "home",
        ...metadata,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El servidor no devolvió la ruta del archivo");
      return { ...response.asset, value };
    },
    [projectId]
  );

  const deferAssetDeletion = React.useCallback(
    async () => ({ ok: true, removed: false, pending: true }),
    []
  );

  const uploadInlineImage = React.useCallback(
    async (path, file, metadata = {}) => {
      try {
        setSavingPath(`asset:${path}`);
        const brandingAssetKey = {
          "theme.logoUrl": "logo",
          "theme.compactLogoUrl": "compact-logo",
          "theme.faviconUrl": "favicon",
        }[String(path || "")];
        const asset = await uploadAssetFile(
          file,
          brandingAssetKey
            ? { ...metadata, sectionKey: "branding", assetKey: brandingAssetKey }
            : metadata
        );
        const next = setValueAtPath(formRef.current, path, asset.value);
        return await stageDraft(next, {
          historyKey: `asset:${path}`,
          forceHistory: true,
        });
      } catch (err) {
        setError(err.message || "No se pudo subir la imagen");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [stageDraft, uploadAssetFile]
  );

  const removeInlineImage = React.useCallback(
    async (path) => {
      const current = formRef.current;

      try {
        const next = setValueAtPath(current, path, "");
        return await stageDraft(next, {
          historyKey: `asset:${path}`,
          forceHistory: true,
        });
      } catch (err) {
        setError(err.message || "No se pudo preparar la eliminación de la imagen");
        throw err;
      }
    },
    [stageDraft]
  );

  const openSettingsAt = React.useCallback((tab = 0, mode = "global") => {
    setAccessibilityOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setSettingsMode(mode);
    setSettingsInitialTab(tab);
    setSettingsOpen(true);
  }, []);

  const openSection = React.useCallback((nextSection, tool = "content") => {
    setAccessibilityOpen(false);
    setSettingsOpen(false);
    setActiveSection(nextSection);
    const layoutSegment = getLandingSegments(formRef.current).find((segment) => segment.id === nextSection);
    const definition = getElementSectionContext(formRef.current, nextSection)?.definition || getSegmentDefinition(layoutSegment?.type || nextSection);
    if (definition?.elementEditingReady === true) {
      setSegmentInspectorOpen(false);
      if (definition.defaultElementId) {
        setElementShellSectionId("");
        setSelectedElement({
          sectionId: nextSection,
          sectionType: layoutSegment?.type || nextSection,
          fieldPath: "",
          elementId: definition.defaultElementId,
          logicalType: "complex",
          previewDevice: previewDevice.id,
        });
      } else {
        setSelectedElement(null);
        setElementShellSectionId(nextSection);
      }
      setElementInspectorOpen(true);
      return;
    }
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setSectionId(nextSection);
    setSegmentInspectorTool(tool);
    setSegmentInspectorImagePath("");
    setSegmentInspectorOpen(true);
  }, [previewDevice.id]);

  const runAction = React.useCallback(
    (action) => {
      if (!action) return;
      if (action.type === "section" && action.section) {
        if (action.section === "identity") {
          openSettingsAt(3, "identity");
          return;
        }
        if (getSegmentDefinition(action.section)) {
          openSection(action.section, action.tool || "content");
          return;
        }
      }
      const requestedTab = Number(action.tab || 0);
      if (requestedTab === 2) openSettingsAt(2, "sections");
      else if (requestedTab === 3) openSettingsAt(3, "identity");
      else openSettingsAt(requestedTab, "global");
    },
    [openSection, openSettingsAt]
  );

  const selectProgressStep = React.useCallback(
    (step) => {
      const firstSection = step?.actions?.find((action) => action.type === "section");
      runAction(firstSection || step?.actions?.[0]);
    },
    [runAction]
  );

  const selectCanvasSection = React.useCallback(
    (nextSection) => {
      const layoutSegment = getLandingSegments(formRef.current).find((segment) => segment.id === nextSection);
      const sectionType = layoutSegment?.type || nextSection;
      const relatedStep = findStepForSection(sectionType, productsEnabled);
      if (relatedStep || layoutSegment || nextSection) setActiveSection(nextSection);
      const definition = getElementSectionContext(formRef.current, nextSection)?.definition || getSegmentDefinition(sectionType);
      if (definition?.elementEditingReady === true) {
        setSettingsOpen(false);
        setAccessibilityOpen(false);
        setSegmentInspectorOpen(false);
        if (definition.defaultElementId) {
          setElementShellSectionId("");
          setSelectedElement({
            sectionId: nextSection,
            sectionType,
            fieldPath: "",
            elementId: definition.defaultElementId,
            logicalType: "complex",
            previewDevice: previewDevice.id,
          });
        } else {
          setSelectedElement(null);
          setElementShellSectionId(nextSection);
        }
        setElementInspectorOpen(true);
      }
    },
    [productsEnabled, previewDevice.id]
  );

  const selectCanvasElement = React.useCallback((nextElement) => {
    if (!nextElement?.sectionId) return;
    setSettingsOpen(false);
    setAccessibilityOpen(false);
    setSegmentInspectorOpen(false);
    setActiveSection(nextElement.sectionId);
    setElementShellSectionId("");
    setSelectedElement(nextElement);
    setElementInspectorOpen(true);
  }, []);

  const openSegmentTool = React.useCallback((nextSection, tool = "content", imagePath = "") => {
    setAccessibilityOpen(false);
    setSettingsOpen(false);
    setActiveSection(nextSection);
    const layoutSegment = getLandingSegments(formRef.current).find((segment) => segment.id === nextSection);
    const definition = getElementSectionContext(formRef.current, nextSection)?.definition || getSegmentDefinition(layoutSegment?.type || nextSection);
    if (definition?.elementEditingReady === true) {
      setSegmentInspectorOpen(false);
      if (definition.defaultElementId) {
        setElementShellSectionId("");
        setSelectedElement({
          sectionId: nextSection,
          sectionType: layoutSegment?.type || nextSection,
          fieldPath: "",
          elementId: definition.defaultElementId,
          logicalType: "complex",
          previewDevice: previewDevice.id,
        });
      } else {
        setSelectedElement(null);
        setElementShellSectionId(nextSection);
      }
      setElementInspectorOpen(true);
      return;
    }
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setSectionId(nextSection);
    setSegmentInspectorTool(tool);
    setSegmentInspectorImagePath(tool === "image" ? String(imagePath || "") : "");
    setSegmentInspectorOpen(true);
  }, [previewDevice.id]);

  const openAccessibilityReview = React.useCallback(() => {
    setPreviewing(false);
    setSettingsOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setActiveSection("");
    setAccessibilityOpen(true);
  }, []);

  const reviewAccessibilityIssue = React.useCallback((issue) => {
    setAccessibilityOpen(false);
    if (!issue?.section) return;
    openSegmentTool(issue.section, issue.tool || "content");
  }, [openSegmentTool]);

  const toggleCanvasSection = React.useCallback(
    (nextSection, nextVisible) => {
      const currentForm = formRef.current;
      const rawSegments = Array.isArray(currentForm?.layout?.segments) ? currentForm.layout.segments : [];
      const customIndex = rawSegments.findIndex(
        (segment) => segment?.id === nextSection && segment?.type === "custom"
      );

      if (customIndex >= 0) {
        commitPath(`layout.segments.${customIndex}.visible`, nextVisible === true);
        if (!nextVisible) {
          setActiveSection("");
          if (sectionId === nextSection) setSegmentInspectorOpen(false);
          if (selectedElement?.sectionId === nextSection || elementShellSectionId === nextSection) {
            setElementInspectorOpen(false);
            setSelectedElement(null);
            setElementShellSectionId("");
          }
        }
        return;
      }

      const definition = getSegmentDefinition(nextSection);
      if (!definition?.visibilityPath) return;

      const changes = buildSegmentVisibilityChanges(nextSection, nextVisible);
      if (!Object.keys(changes).length) return;

      commitMany(changes);
      if (!nextVisible) {
        setActiveSection("");
        if (sectionId === nextSection) setSegmentInspectorOpen(false);
        if (selectedElement?.sectionId === nextSection || elementShellSectionId === nextSection) {
          setElementInspectorOpen(false);
          setSelectedElement(null);
          setElementShellSectionId("");
        }
      }
    },
    [commitMany, commitPath, elementShellSectionId, sectionId, selectedElement?.sectionId]
  );

  const changeEditorSurface = React.useCallback((nextSurface) => {
    const allowed = editorSurfaceOptions.some((option) => option.id === nextSurface);
    if (!allowed) return;
    setPreviewing(false);
    setSettingsOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setAccessibilityOpen(false);
    setActiveSection("");
    setEditorSurface(nextSurface);
  }, [editorSurfaceOptions]);

  const togglePreview = React.useCallback(() => {
    setSettingsOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setActiveSection("");
    setPreviewing((current) => !current);
  }, []);

  const openSecurePreview = React.useCallback(async () => {
    if (!publicUrl) {
      setError("La página pública todavía no tiene una URL disponible para la vista previa.");
      return null;
    }

    const previewWindow = openPreviewPlaceholder();
    if (!previewWindow) {
      setError("El navegador bloqueó la nueva pestaña. Habilitá las ventanas emergentes e intentá nuevamente.");
      return null;
    }

    try {
      setOpeningPreview(true);
      setError("");

      if (dirty) {
        const savedDraft = await saveDraft();
        if (!savedDraft) {
          previewWindow.close();
          return null;
        }
      }

      const data = await settingsApi.createPreviewToken(projectId);
      const preview = data?.preview || null;
      const previewUrl = buildProjectSecurePreviewUrl(
        effectiveProject,
        preview,
        getApiBaseUrl()
      );

      if (!previewUrl) {
        throw new Error("El servidor no devolvió una vista previa válida para esta página.");
      }
      if (previewWindow.closed) {
        throw new Error("La pestaña de vista previa se cerró antes de completar la apertura.");
      }

      previewWindow.location.replace(previewUrl);
      setMessage(
        `Vista previa segura abierta para el borrador v${preview?.draftRevision || formRef.current?.draftRevision || 1}.`
      );
      return preview;
    } catch (err) {
      if (!previewWindow.closed) previewWindow.close();
      setError(err.message || "No se pudo abrir la vista previa segura");
      return null;
    } finally {
      setOpeningPreview(false);
    }
  }, [dirty, effectiveProject, projectId, publicUrl, saveDraft]);

  const loadPublishedRevisions = React.useCallback(async () => {
    try {
      setRevisionsLoading(true);
      setRevisionsError("");
      const data = await settingsApi.listRevisions(projectId);
      setRevisions(Array.isArray(data?.revisions) ? data.revisions : []);
      return data;
    } catch (err) {
      setRevisionsError(
        formatApiError(err, "No se pudo cargar el historial de publicaciones")
      );
      return null;
    } finally {
      setRevisionsLoading(false);
    }
  }, [projectId]);

  const openPublishedRevisions = React.useCallback(() => {
    setRevisionsOpen(true);
    loadPublishedRevisions();
  }, [loadPublishedRevisions]);

  const restorePublishedRevision = React.useCallback(
    async (revision) => {
      const siteRevision = Number(revision?.siteRevision || 0);
      if (!Number.isInteger(siteRevision) || siteRevision < 1) {
        setError("La publicación seleccionada ya no tiene una revisión válida.");
        return null;
      }

      const warning = dirtyRef.current
        ? "Tenés cambios locales sin guardar. La recuperación reemplazará el borrador guardado; tus cambios actuales quedarán disponibles con Deshacer durante esta sesión. La web publicada no cambiará."
        : "La recuperación reemplazará el borrador guardado con esta publicación. La web publicada no cambiará hasta que vuelvas a publicar.";
      if (!window.confirm(`${warning}\n\n¿Querés continuar?`)) return null;

      const beforeRestore = formRef.current;
      try {
        setRestoringSiteRevision(siteRevision);
        setRevisionsError("");
        setError("");
        const data = await settingsApi.restoreRevision(projectId, siteRevision);
        const merged = mergeSettings(data?.settings);
        const contentChanged = !sameEditorValue(
          draftSettingsPayload(beforeRestore, productsEnabled),
          draftSettingsPayload(merged, productsEnabled)
        );

        if (contentChanged) {
          recordUndoCheckpoint(
            beforeRestore,
            `restore:${siteRevision}`,
            true
          );
        }

        pendingAssetDeletesRef.current.clear();
        savedDraftRef.current = merged;
        formRef.current = merged;
        setForm(merged);
        dirtyRef.current = false;
        setDirty(false);
        setRevisionsOpen(false);

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );

        setMessage(
          contentChanged
            ? "La versión elegida se recuperó como borrador. La web publicada no cambió."
            : "El borrador ya coincidía con esa publicación. No se creó una revisión duplicada."
        );
        return data;
      } catch (err) {
        const message = formatApiError(
          err,
          "No se pudo recuperar la publicación seleccionada"
        );
        setRevisionsError(message);
        setError(message);
        return null;
      } finally {
        setRestoringSiteRevision(null);
      }
    },
    [productsEnabled, projectId, recordUndoCheckpoint]
  );

  const applyPublicStatus = React.useCallback(
    async (targetStatus) => {
      const publicStatus = String(targetStatus || "").trim().toLowerCase();
      if (!["draft", "published", "maintenance"].includes(publicStatus)) {
        setError("El estado público seleccionado no es válido.");
        return null;
      }
      if (statusTransition) return null;

      if (dirtyRef.current) {
        const savedDraft = await saveDraft();
        if (!savedDraft) return null;
      }

      try {
        setStatusTransition(publicStatus);
        setError("");
        setDeploymentMonitorError("");
        const data =
          publicStatus === "published"
            ? await settingsApi.publish(projectId)
            : await settingsApi.setPublicStatus(projectId, publicStatus);
        const publication = data.publication || {};
        const returnedSettings = data.settings || data.publishedSettings || null;
        const base = returnedSettings
          ? mergeSettings(returnedSettings)
          : formRef.current;
        const revision = Number(
          data.revision ?? publication.revision ?? returnedSettings?.revision ?? base.revision ?? 1
        );
        const draftRevision = Number(
          data.draftRevision ??
            publication.draftRevision ??
            returnedSettings?.draftRevision ??
            base.draftRevision ??
            revision
        );
        const publishedRevision = Number(
          data.publishedRevision ??
            publication.publishedRevision ??
            returnedSettings?.publishedRevision ??
            base.publishedRevision ??
            0
        );
        const nextForm = {
          ...base,
          revision,
          draftRevision,
          publicStatus:
            data.publicStatus ||
            publication.publicStatus ||
            returnedSettings?.publicStatus ||
            publicStatus,
          publishedRevision,
        };
        formRef.current = nextForm;
        setForm(nextForm);
        dirtyRef.current = false;
        setDirty(false);

        const deployment = deploymentFromPublishResponse(data);
        let nextDeploymentJob = deploymentJobFromPublishResponse(data);
        if (!nextDeploymentJob && deployment?.refreshQueued === true) {
          try {
            const jobsData = await projectsApi.listDeploymentJobs(projectId, {
              type: "social_preview_refresh",
              limit: 5,
            });
            const jobs = Array.isArray(jobsData?.jobs) ? jobsData.jobs : [];
            nextDeploymentJob = jobs[0] || null;
          } catch (jobError) {
            setDeploymentMonitorError(
              formatApiError(
                jobError,
                "La publicación se completó, pero no se pudo consultar su despliegue"
              )
            );
          }
        }
        setDeploymentJob(nextDeploymentJob);
        const returnedProject = data.project || null;
        if (returnedProject || deployment) {
          const nextProject = {
            ...(effectiveProject || {}),
            ...(returnedProject || {}),
            deployment: {
              ...(effectiveProject?.deployment || {}),
              ...(returnedProject?.deployment || {}),
              ...(deployment || {}),
            },
          };
          setProject(nextProject);
          setActiveProject(nextProject);
        }

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: nextForm },
          })
        );

        if (nextForm.publicStatus === "published") {
          const deploymentStatus = String(deployment?.status || "").trim();
          const deploymentQueued = deployment?.refreshQueued === true;
          const publicationChanged = publication.changed !== false;
          setMessage(
            !publicationChanged
              ? `El borrador v${draftRevision} ya era la versión publicada. No se creó otra revisión.`
              : deploymentQueued
                ? `Revisión ${publishedRevision} publicada. El despliegue quedó en cola y se seguirá desde el editor.`
                : deploymentStatus === "live"
                  ? `Sitio publicado en la revisión ${publishedRevision}.`
                  : `Revisión ${publishedRevision} publicada. Estado del despliegue: ${deploymentStatus || "sin despliegue automático"}.`
          );
        } else if (nextForm.publicStatus === "maintenance") {
          setMessage(
            `Modo mantenimiento activado. La publicación ${publishedRevision || "anterior"} se conservó.`
          );
        } else {
          setMessage(
            "Sitio oculto como borrador. La última publicación y el historial se conservaron."
          );
        }
        return data;
      } catch (err) {
        setError(formatApiError(err, "No se pudo cambiar el estado público del sitio"));
        return null;
      } finally {
        setStatusTransition("");
      }
    },
    [effectiveProject, projectId, saveDraft, setActiveProject, statusTransition]
  );

  const validatePublication = React.useCallback(async () => {
    if (dirtyRef.current) {
      setPublicationValidation(null);
      setPublicationValidationError("Guardá el borrador antes de validarlo para publicación.");
      return null;
    }
    try {
      setPublicationValidationLoading(true);
      setPublicationValidationError("");
      const data = await settingsApi.validatePublication(projectId);
      const validation = data?.validation || null;
      setPublicationValidation(validation);
      return validation;
    } catch (err) {
      const message = formatApiError(err, "No se pudo validar el borrador antes de publicar");
      setPublicationValidation(null);
      setPublicationValidationError(message);
      return null;
    } finally {
      setPublicationValidationLoading(false);
    }
  }, [projectId]);

  const publishDraft = React.useCallback(async () => {
    if (dirtyRef.current) {
      setError("Guardá el borrador antes de publicarlo.");
      return null;
    }
    const validation = await validatePublication();
    if (!validation) {
      setError("No se pudo completar la validación previa a la publicación.");
      return null;
    }
    if (validation.canPublish !== true) {
      setError("Corregí los problemas bloqueantes indicados antes de publicar.");
      return null;
    }
    return applyPublicStatus("published");
  }, [applyPublicStatus, validatePublication]);

  const leaveEditor = () => {
    if (dirty && !window.confirm("Hay cambios sin guardar. ¿Querés salir del editor igualmente?")) {
      return;
    }
    navigate(`/dashboard/proyectos/${projectId}/configuracion/general`);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.3} alignItems="center">
          <CircularProgress sx={{ color: BRAND.orange }} />
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
            Preparando la página editable…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (effectiveProject && !landingEnabled) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2, bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.5} sx={{ maxWidth: 520, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
            La página web no está habilitada
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,.62)" }}>
            Este proyecto todavía no tiene habilitado el módulo de landing.
          </Typography>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={leaveEditor} sx={{ alignSelf: "center" }}>
            Volver al panel
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F4F5F7" }}>
      <LandingStudioToolbar
        title={form.business?.name || effectiveProject?.name || "Tu página web"}
        publicStatus={form.publicStatus}
        revision={form.revision}
        draftRevision={form.draftRevision}
        publishedRevision={form.publishedRevision}
        deployment={effectiveProject?.deployment}
        deploymentJob={deploymentJob}
        deploymentError={deploymentMonitorError}
        deploymentChecking={deploymentChecking}
        deploymentAction={deploymentAction}
        device={device}
        saving={saving}
        publishing={publishing}
        statusChanging={changingPublicStatus}
        previewing={previewing}
        openingPreview={openingPreview}
        hasUnsavedChanges={dirty}
        publicationValidation={publicationValidation}
        publicationValidationLoading={publicationValidationLoading}
        publicationValidationError={publicationValidationError}
        accessibilityIssueCount={accessibilityAudit.issues.length}
        canOpenPublic={Boolean(publicUrl)}
        designTokens={form.designTokens}
        theme={form.theme}
        onBack={leaveEditor}
        onChangeDevice={setDevice}
        onOpenSettings={(tab = 0) => openSettingsAt(tab, "global")}
        onOpenSections={() => openSettingsAt(2, "sections")}
        onChangeDesignTokens={(nextTokens) => {
          const heading = nextTokens?.typography?.headingFontFamily || form?.theme?.headingFontFamily || "Exo 2";
          const body = nextTokens?.typography?.bodyFontFamily || nextTokens?.typography?.fontFamily || form?.theme?.bodyFontFamily || "Asap";
          const tokenColors = nextTokens?.colors || {};
          return commitMany({
            designTokens: nextTokens,
            ...(tokenColors.primary ? { "theme.primaryColor": tokenColors.primary } : {}),
            ...(tokenColors.secondary ? { "theme.secondaryColor": tokenColors.secondary } : {}),
            ...(tokenColors.background ? { "theme.backgroundColor": tokenColors.background } : {}),
            ...(tokenColors.surface ? { "theme.surfaceColor": tokenColors.surface } : {}),
            ...(tokenColors.text ? { "theme.textColor": tokenColors.text } : {}),
            ...(tokenColors.mutedText ? { "theme.mutedTextColor": tokenColors.mutedText } : {}),
            ...(tokenColors.divider ? { "theme.dividerColor": tokenColors.divider } : {}),
            "theme.fontFamily": body,
            "theme.headingFontFamily": heading,
            "theme.bodyFontFamily": body,
            "theme.googleFontsUrl": buildGoogleFontsUrl(heading, body),
          });
        }}
        onSaveDraft={saveDraft}
        onTogglePreview={togglePreview}
        onOpenSecurePreview={openSecurePreview}
        onValidatePublication={validatePublication}
        onOpenAccessibility={openAccessibilityReview}
        onPublish={publishDraft}
        onOpenPublic={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}
        onRefreshDeployment={() => refreshDeploymentState()}
        onRetryDeployment={retryDeployment}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undoLastEdit}
        onRedo={redoLastEdit}
        onOpenHistory={openPublishedRevisions}
        editorSurface={editorSurface}
        surfaceOptions={editorSurfaceOptions}
        onChangeSurface={changeEditorSurface}
        landingToolsAvailable={editorSurface === "landing"}
      />

      {previewing ? (
        <Box
          component="main"
          sx={{
            position: "fixed",
            inset: "64px 0 0",
            overflow: "auto",
            bgcolor: "#E9ECF0",
          }}
        >
          <Box
            sx={{
              minWidth: `${previewDevice.width + previewHorizontalGutter}px`,
              minHeight: "100%",
              py: 1.5,
              px: previewDevice.id === "mobile" ? 0 : 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                width: `${previewDevice.width}px`,
                mb: 1,
                px: 0.25,
                color: "rgba(25,36,47,.64)",
                fontWeight: 900,
              }}
            >
              {previewDevice.label} · {previewDevice.width}px · vista limpia del borrador
            </Typography>
            <Box
              data-landing-preview-device={previewDevice.id}
              sx={{
                width: `${previewDevice.width}px`,
                bgcolor: "#fff",
                borderRadius: previewDevice.id === "mobile" ? 2.5 : previewDevice.id === "tablet" ? 2 : 1.5,
                outline: "1px solid rgba(25,36,47,.12)",
                boxShadow: previewDevice.id === "desktop"
                  ? "0 14px 42px rgba(25,36,47,.12)"
                  : "0 18px 48px rgba(25,36,47,.18)",
                overflow: "hidden",
              }}
            >
              <LandingCanvas
                form={form}
                device={previewDevice.id}
                productsEnabled={productsEnabled}
                activeSection=""
                studioMode={false}
              />
            </Box>
          </Box>
        </Box>
      ) : editorSurface === "products" ? (
        <ProductsPageEditor
          form={form}
          device={device}
          saving={saving}
          onCommit={commitPath}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
        />
      ) : editorSurface === "product" ? (
        <ProductPageEditor
          form={form}
          device={device}
          saving={saving}
          onCommit={commitPath}
        />
      ) : editorSurface === "notifications" ? (
        <WebPushPromptEditor
          form={form}
          device={device}
          saving={saving}
          onCommit={commitPath}
        />
      ) : (
        <>
          <LandingStudioCanvasStage
            form={form}
            device={device}
            productsEnabled={productsEnabled}
            activeSection={activeSection}
            selectedElement={selectedElement}
            saving={saving}
            inspectorOpen={segmentInspectorOpen || elementInspectorOpen}
            onSelectSection={selectCanvasSection}
            onSelectElement={selectCanvasElement}
            onCommit={commitPath}
            onCommitMany={commitMany}
            onUploadImage={uploadInlineImage}
            onRemoveImage={removeInlineImage}
            onOpenSegmentTool={openSegmentTool}
            onToggleSection={toggleCanvasSection}
          />

          <LandingStudioQuestionDock
            question={question}
            progress={progress}
            onResolve={() => runAction(question.action)}
            onSelectStep={selectProgressStep}
          />
        </>
      )}

      {!previewing && editorSurface === "landing" ? (
        <LandingElementInspector
          open={elementInspectorOpen}
          selectedElement={selectedElement}
          shellSectionId={elementShellSectionId}
          form={form}
          saving={saving}
          onClose={() => {
            setElementInspectorOpen(false);
            setSelectedElement(null);
            setElementShellSectionId("");
          }}
          onCommit={commitPath}
          onToggleSection={toggleCanvasSection}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
          onOpenGlobalPalette={() => openSettingsAt(0, "global")}
        />
      ) : null}

      {!previewing && editorSurface === "landing" ? (
        <LandingSegmentInspector
          open={segmentInspectorOpen}
          sectionId={sectionId}
          initialTool={segmentInspectorTool}
          initialImagePath={segmentInspectorImagePath}
          form={form}
          productsEnabled={productsEnabled}
          saving={saving}
          onClose={() => setSegmentInspectorOpen(false)}
          onCommit={commitPath}
          onCommitMany={commitMany}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
        />
      ) : null}

      {!previewing ? (
        <LandingSettingsDialog
          open={settingsOpen}
          form={form}
          productsEnabled={productsEnabled}
          saving={savingPath === "settings"}
          changingPublicStatus={changingPublicStatus}
          initialTab={settingsInitialTab}
          mode={settingsMode}
          showTrialGuide={!progress.complete}
          onClose={() => setSettingsOpen(false)}
          onSave={saveSettingsDialog}
          onChangePublicStatus={applyPublicStatus}
          onUploadAsset={uploadAssetFile}
          onDeleteAsset={deferAssetDeletion}
        />
      ) : null}

      <LandingAccessibilityDrawer
        open={accessibilityOpen}
        audit={accessibilityAudit}
        onClose={() => setAccessibilityOpen(false)}
        onReviewIssue={reviewAccessibilityIssue}
      />

      <LandingRevisionsDrawer
        open={revisionsOpen}
        revisions={revisions}
        loading={revisionsLoading}
        error={revisionsError}
        restoringSiteRevision={restoringSiteRevision}
        currentPublishedRevision={form.publishedRevision}
        onClose={() => setRevisionsOpen(false)}
        onReload={loadPublishedRevisions}
        onRestore={restorePublishedRevision}
      />

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={2200}
        onClose={() => setMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMessage("")} sx={{ fontWeight: 850 }}>
          {message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5200}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ fontWeight: 850 }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
